// @ts-nocheck
import { Phone, PhoneOff, Mic, MicOff, PhoneIncoming, Clock, Volume2, VolumeX, Pause, Play, Hash, ArrowRight, WifiOff } from 'lucide-react'
import { useCallStore, formatCallDuration, getCallStatusText } from '../stores/callStore'
import { useEffect, useState, useRef, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'
import { useApi } from '../hooks/useApi'
import { useTwilioDevice } from '../hooks/useTwilioDevice'
import { DTMFKeypad } from './DTMFKeypad'
import { logger } from '../utils/logger'

/**
 * グローバル通話状態バー（左上固定Widget風）
 * アプリケーション全体で通話状態を表示
 * React.memoで最適化: 必要な状態が変更された時のみ再レンダリング
 */
const CallStatusBar = memo(function CallStatusBar() {
  // Zustand Selector最適化: 必要な値のみを個別に取得
  const callStatus = useCallStore((state) => state.callStatus)
  const callerNumber = useCallStore((state) => state.callerNumber)
  const customerName = useCallStore((state) => state.customerName)
  const phoneNumberName = useCallStore((state) => state.phoneNumberName)
  const callDuration = useCallStore((state) => state.callDuration)
  const isMuted = useCallStore((state) => state.isMuted)
  const isSpeakerOn = useCallStore((state) => state.isSpeakerOn)
  const deviceStatus = useCallStore((state) => state.deviceStatus)
  const heldCalls = useCallStore((state) => state.heldCalls)
  const currentCallId = useCallStore((state) => state.currentCallId)
  const currentCallSid = useCallStore((state) => state.currentCallSid)
  const incomingCall = useCallStore((state) => state.incomingCall)
  const acceptIncomingCall = useCallStore((state) => state.acceptIncomingCall)
  const rejectIncomingCall = useCallStore((state) => state.rejectIncomingCall)
  const endCall = useCallStore((state) => state.endCall)
  const toggleMute = useCallStore((state) => state.toggleMute)
  const toggleSpeaker = useCallStore((state) => state.toggleSpeaker)
  const updateCallDuration = useCallStore((state) => state.updateCallDuration)
  const ignoreCall = useCallStore((state) => state.ignoreCall)
  const dismissRingingCall = useCallStore((state) => state.dismissRingingCall)
  const setCallStatus = useCallStore((state) => state.setCallStatus)
  const setCallerNumber = useCallStore((state) => state.setCallerNumber)
  const setCurrentCallId = useCallStore((state) => state.setCurrentCallId)
  const startOutgoingCall = useCallStore((state) => state.startOutgoingCall)
  const twilioDevice = useCallStore((state) => state.twilioDevice)
  const ringAudioEnabled = useCallStore((state) => state.ringAudioEnabled)
  const markCallAnswered = useCallStore((state) => state.markCallAnswered)
  const activeCall = useCallStore((state) => state.activeCall)
  const holdQueueName = useCallStore((state) => state.holdQueueName)
  const incomingInternalCall = useCallStore((state) => state.incomingInternalCall)
  const internalCallStatus = useCallStore((state) => state.internalCallStatus)
  const currentInternalCallId = useCallStore((state) => state.currentInternalCallId)
  const clearIncomingInternalCall = useCallStore((state) => state.clearIncomingInternalCall)
  const isWaitingForResumedCall = useCallStore((state) => state.isWaitingForResumedCall)
  const resumedCallInfo = useCallStore((state) => state.resumedCallInfo)
  const clearWaitingForResumedCall = useCallStore((state) => state.clearWaitingForResumedCall)
  const navigate = useNavigate()
  const api = useApi()
  const { answerQueueCall, holdCall, resumeHeldCall, transferToOperator } = useTwilioDevice()
  const isAnswerPending = callStatus === 'connecting'
  const [answering, setAnswering] = useState(false)
  const [isHolding, setIsHolding] = useState(false)
  const [isResuming, setIsResuming] = useState(false)
  const [isTransferring, setIsTransferring] = useState(false)
  const audioRef = useRef(null)
  const canPlayAudio = ringAudioEnabled
  const clearedRef = useRef(false)
  const [phoneMap, setPhoneMap] = useState({})
  const [, forceUpdate] = useState(0)
  const holdDelayTimerRef = useRef(null)
  const [canHold, setCanHold] = useState(false)
  const [isDTMFKeypadOpen, setIsDTMFKeypadOpen] = useState(false)

  const displayNumber = (num) => {
    // 顧客名がある場合は顧客名を優先表示
    if (customerName) {
      return customerName
    }
    const formatted = formatPhoneNumberForDisplay(num)
    const label = phoneMap[num]?.name || phoneMap[num]?.label
    return label ? `${label} (${formatted})` : formatted
  }
  
  // 電話番号のみを表示（顧客名がある場合のサブテキスト用）
  const displayPhoneOnly = (num) => {
    return formatPhoneNumberForDisplay(num)
  }

  // 代表番号の名称を取得してマップ化（着信先の名前を併記するため）
  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      try {
        const data = await api.get('/api/phone-numbers')
        const items = data?.items || []
        const map = items.reduce((acc, cur) => {
          if (cur.phoneNumber) {
            acc[cur.phoneNumber] = { name: cur.name || cur.label || '' }
          }
          return acc
        }, {})
        setPhoneMap(map)
      } catch (err) {
        // 表示補助のみのため静かに失敗
      }
    }
    fetchPhoneNumbers()
  }, [api])

  // 着信時の音声再生
  useEffect(() => {
    if (callStatus === 'ringing' && canPlayAudio) {
      if (!audioRef.current) {
        audioRef.current = new Audio('/ring.mp3')
        audioRef.current.loop = true
        audioRef.current.volume = 0.5
      }
      
      const playPromise = audioRef.current.play()
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            logger.debug('[CallStatusBar] 着信音再生開始')
          })
          .catch(error => {
            logger.warn('[CallStatusBar] 着信音の自動再生がブロックされました:', error)
          })
      }
    } else {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
    }
  }, [callStatus, canPlayAudio])

  // 通話時間の定期更新
  useEffect(() => {
    if (callStatus === 'connected' || internalCallStatus === 'connected') {
      const interval = setInterval(() => {
        updateCallDuration()
        forceUpdate(n => n + 1) // 再レンダリング
      }, 1000)
      
      return () => clearInterval(interval)
    }
  }, [callStatus, internalCallStatus, updateCallDuration])

  // マウント直後のみ保留ボタンを数秒待機させる
  useEffect(() => {
    setCanHold(false)
    holdDelayTimerRef.current = setTimeout(() => setCanHold(true), 3000)
    return () => {
      if (holdDelayTimerRef.current) {
        clearTimeout(holdDelayTimerRef.current)
        holdDelayTimerRef.current = null
      }
    }
  }, [])

  const handleHold = async () => {
    const callId = currentCallId
    if (!callId) return
    setIsHolding(true)
    try {
      await holdCall(callId)
    } catch (err) {
      logger.error('保留エラー:', err)
    } finally {
      setIsHolding(false)
    }
  }

  const handleResume = async () => {
    if (!holdQueueName) return
    setIsResuming(true)
    try {
      const result = await resumeHeldCall(holdQueueName)
      // 通話が既に切断されていた場合
      if (result && result.success === false) {
        logger.info('保留解除結果:', result.message)
        // 状態は既にクリアされているので、UIは自動的に更新される
      }
    } catch (err) {
      logger.error('保留解除エラー:', err)
    } finally {
      setIsResuming(false)
    }
  }

  // 内線通話中に保留を解除（内線を切断してから自分に転送）
  const handleResumeWithInternalDisconnect = async () => {
    // holdQueueNameを先にキャプチャ（disconnect後に状態がクリアされる可能性があるため）
    const queueNameToResume = holdQueueName
    if (!queueNameToResume) {
      logger.error('[CallStatusBar] holdQueueNameがありません')
      return
    }

    logger.debug('[CallStatusBar] 保留解除（自分に転送）開始:', { queueNameToResume })
    setIsResuming(true)

    try {
      // 1. 内線通話を切断（store action経由）
      useCallStore.getState().endInternalCall()

      // 2. 少し待ってから保留を解除（内線切断の完了を待つ）
      await new Promise(resolve => setTimeout(resolve, 800))

      // 3. 保留を解除（キャプチャしたキュー名を使用）
      logger.debug('[CallStatusBar] 保留解除APIを呼び出し:', queueNameToResume)
      const result = await resumeHeldCall(queueNameToResume)
      logger.debug('[CallStatusBar] 保留解除結果:', result)

      if (result && result.success === false) {
        logger.info('保留解除結果:', result.message)
      }
    } catch (err) {
      logger.error('保留解除エラー:', err)
      alert(`保留解除に失敗しました: ${err.message}`)
    } finally {
      setIsResuming(false)
    }
  }

  // 内線通話中に保留中の通話を相手に転送
  const handleTransferToInternalPeer = async () => {
    // 値を先にキャプチャ
    const queueNameToTransfer = holdQueueName
    // internalCallPeerUserIdを使用（内線通話接続時に保存される）
    const peerUserId = useCallStore.getState().internalCallPeerUserId

    if (!queueNameToTransfer) {
      logger.error('[CallStatusBar] holdQueueNameがありません')
      return
    }

    if (!peerUserId) {
      logger.error('[CallStatusBar] 内線通話の相手ユーザーIDが取得できません:', {
        internalCallPeerUserId: peerUserId,
        currentInternalCallId,
      })
      alert('内線通話の相手情報が取得できません')
      return
    }

    logger.debug('[CallStatusBar] 内線相手に転送開始:', { queueNameToTransfer, targetOperatorId: peerUserId })
    setIsTransferring(true)

    try {
      // 1. 内線通話を切断（store action経由）
      useCallStore.getState().endInternalCall()

      // 2. 少し待ってから転送（内線切断の完了を待つ）
      await new Promise(resolve => setTimeout(resolve, 500))

      // 4. 保留中の通話を内線相手に転送
      logger.debug('[CallStatusBar] transferToOperator APIを呼び出し:', { queueNameToTransfer, targetOperatorId: peerUserId })
      await transferToOperator(queueNameToTransfer, peerUserId)

      logger.info('[CallStatusBar] 転送成功')
    } catch (err) {
      logger.error('転送エラー:', err)
      alert(`転送に失敗しました: ${err.message}`)
    } finally {
      setIsTransferring(false)
    }
  }

  // 待受中かつcallIdもなく、内線通話もなく、保留中でもなく、着信待ちでもない場合のみ何も表示しない
  if (callStatus === 'idle' && deviceStatus === 'ready' && !currentCallId && !incomingInternalCall && internalCallStatus === 'idle' && !holdQueueName && !isWaitingForResumedCall) {
    return (
      <>
        {/* DTMFキーパッド */}
        <DTMFKeypad
          isOpen={isDTMFKeypadOpen}
          onClose={() => setIsDTMFKeypadOpen(false)}
        />
      </>
    )
  }
  
  // callStatusがidleでもcurrentCallIdがある場合は通話バーを表示（エッジケース対応）
  const effectiveCallStatus = (callStatus === 'idle' && currentCallId) ? 'connecting' : callStatus
  
  // デバイスエラー
  if (deviceStatus === 'error') {
    return (
      <>
        <div className="fixed top-4 left-4 sm:left-[5vw] w-[calc(100vw-2rem)] sm:w-[25vw] sm:min-w-[300px] sm:max-w-[400px] bg-red-600 text-white rounded-xl shadow-2xl z-[60] p-4">
          <div className="flex items-center gap-3">
            <PhoneOff className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium text-sm">通話システムエラー</span>
          </div>
        </div>
        {/* DTMFキーパッド */}
        <DTMFKeypad
          isOpen={isDTMFKeypadOpen}
          onClose={() => setIsDTMFKeypadOpen(false)}
        />
      </>
    )
  }
  
  // デバイスオフライン
  if (deviceStatus === 'offline') {
    return (
      <>
        <div className="fixed top-4 left-4 sm:left-[5vw] w-[calc(100vw-2rem)] sm:w-[25vw] sm:min-w-[300px] sm:max-w-[400px] bg-orange-500 text-white rounded-xl shadow-2xl z-[60] p-4">
          <div className="flex items-center gap-3">
            <WifiOff className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium text-sm">ネットワーク切断中 — 発着信できません</span>
          </div>
        </div>
        {/* DTMFキーパッド */}
        <DTMFKeypad
          isOpen={isDTMFKeypadOpen}
          onClose={() => setIsDTMFKeypadOpen(false)}
        />
      </>
    )
  }

  // デバイス初期化中
  if (deviceStatus === 'initializing') {
    return (
      <>
        <div className="fixed top-4 left-4 sm:left-[5vw] w-[calc(100vw-2rem)] sm:w-[25vw] sm:min-w-[300px] sm:max-w-[400px] bg-blue-600 text-white rounded-xl shadow-2xl z-[60] p-4">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white flex-shrink-0"></div>
            <span className="font-medium text-sm">通話システム初期化中...</span>
          </div>
        </div>
        {/* DTMFキーパッド */}
        <DTMFKeypad
          isOpen={isDTMFKeypadOpen}
          onClose={() => setIsDTMFKeypadOpen(false)}
        />
      </>
    )
  }
  
  // 内線着信中（紫色で表示して外線と区別）- 点滅アニメーション
  if (incomingInternalCall) {
    return (
      <>
        <div className="fixed top-4 left-4 sm:left-[5vw] w-[calc(100vw-2rem)] sm:w-[25vw] sm:min-w-[300px] sm:max-w-[400px] bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-xl shadow-2xl z-[60] animate-pulse-shadow-purple">
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <PhoneIncoming className="w-5 h-5 animate-pulse flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">内線着信</div>
              <div className="text-xs text-white/90 truncate">{incomingInternalCall.callerName}から</div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={async () => {
                try {
                  if (incomingInternalCall.twilioCall) {
                    const callerName = incomingInternalCall.callerName
                    const internalCallId = incomingInternalCall.internalCallId
                    
                    await api.post(`/api/internal-calls/${internalCallId}/answer`)
                    incomingInternalCall.twilioCall.accept()
                    
                    setCallerNumber(callerName)
                    setCurrentCallId(internalCallId)
                  } else {
                    throw new Error('Twilio Callオブジェクトがありません')
                  }
                } catch (err) {
                  logger.error('内線応答エラー:', err)
                  alert(err.message || '内線通話の応答に失敗しました')
                  clearIncomingInternalCall()
                }
              }}
              disabled={isAnswerPending}
              className="flex-1 px-3 py-2 bg-green-500 hover:bg-green-600 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Phone className="w-4 h-4" />
              {isAnswerPending ? '処理中...' : '応答'}
            </button>
            <button
              onClick={async () => {
                try {
                  if (incomingInternalCall.twilioCall) {
                    incomingInternalCall.twilioCall.reject()
                  }
                  await api.post(`/api/internal-calls/${incomingInternalCall.internalCallId}/reject`)
                  clearIncomingInternalCall()
                } catch (err) {
                  logger.error('内線拒否エラー:', err)
                  clearIncomingInternalCall()
                }
              }}
              className="flex-1 px-3 py-2 bg-red-500 hover:bg-red-600 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
            >
              <PhoneOff className="w-4 h-4" />
              拒否
            </button>
          </div>
        </div>
        </div>
        {/* DTMFキーパッド */}
        <DTMFKeypad
          isOpen={isDTMFKeypadOpen}
          onClose={() => setIsDTMFKeypadOpen(false)}
        />
      </>
    )
  }
  
  // 外線着信中（オレンジ色）- 点滅アニメーション
  if (effectiveCallStatus === 'ringing') {
    return (
      <>
        <div className="fixed top-4 left-4 sm:left-[5vw] w-[calc(100vw-2rem)] sm:w-[25vw] sm:min-w-[300px] sm:max-w-[400px] bg-gradient-to-br from-orange-500 to-amber-600 text-white rounded-xl shadow-2xl z-[60] animate-pulse-shadow-orange">
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <PhoneIncoming className="w-5 h-5 animate-pulse flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm mb-1">着信中</div>
              {/* 着信先電話番号の名前を最も目立つように表示 */}
              {phoneNumberName && (
                <div className="mb-2 px-2 py-1 bg-white/20 rounded-md border border-white/30">
                  <div className="text-[10px] text-white/70 uppercase tracking-wide mb-0.5">着信先</div>
                  <div className="text-xl text-white font-black truncate">{phoneNumberName}</div>
                </div>
              )}
              {customerName ? (
                <>
                  <div className="text-lg text-white font-bold truncate mb-0.5">{customerName}</div>
                  <div className="text-sm text-white/80 truncate">{displayPhoneOnly(callerNumber)}</div>
                </>
              ) : (
                <div className="text-base text-white font-medium truncate">{displayNumber(callerNumber)}</div>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={async () => {
                logger.debug('[CallStatusBar] 応答ボタンクリック:', {
                  incomingCall,
                  currentCallId,
                  callStatus,
                  callerNumber
                })
                
                if (incomingCall) {
                  acceptIncomingCall()
                  return
                }

                if (!currentCallId) {
                  logger.error('[CallStatusBar] currentCallIdが設定されていません')
                  return
                }
                
                try {
                  logger.debug('[CallStatusBar] 着信応答開始（新方式）:', { currentCallId })
                  await answerQueueCall(currentCallId)
                  logger.debug('[CallStatusBar] 着信応答完了')
                } catch (err) {
                  logger.error('[CallStatusBar] 応答エラー:', err)
                  alert(err.message || '着信への応答に失敗しました')
                }
              }}
              disabled={isAnswerPending}
              className="flex-1 px-3 py-2 bg-white text-orange-600 hover:bg-orange-50 rounded-lg font-bold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isAnswerPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                  処理中...
                </>
              ) : (
                <>
                  <Phone className="w-4 h-4" />
                  応答
                </>
              )}
            </button>
            <button
              onClick={() => {
                if (incomingCall) {
                  rejectIncomingCall()
                } else {
                  dismissRingingCall()
                }
              }}
              className="flex-1 px-3 py-2 bg-orange-700/80 hover:bg-orange-800 rounded-lg font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <PhoneOff className="w-4 h-4" />
              {incomingCall ? '拒否' : '閉じる'}
            </button>
          </div>
        </div>
        </div>
        {/* DTMFキーパッド */}
        <DTMFKeypad
          isOpen={isDTMFKeypadOpen}
          onClose={() => setIsDTMFKeypadOpen(false)}
        />
      </>
    )
  }
  
  // 通話中（内線通話の場合は紫系、外線は緑系）
  if (effectiveCallStatus === 'connected' || internalCallStatus === 'connected') {
    const isInternalCall = internalCallStatus === 'connected'
    const bgColor = isInternalCall
      ? 'from-purple-600 to-indigo-600'
      : 'from-emerald-600 to-teal-600'

    // デバッグ: 内線通話中の状態をログ出力
    if (isInternalCall) {
      logger.debug('[CallStatusBar] 内線通話中の状態:', {
        isInternalCall,
        holdQueueName,
        hasActiveCall: !!activeCall,
        internalCallStatus,
        callStatus: effectiveCallStatus,
      })
    }

    return (
      <>
        <div className={`fixed top-4 left-4 sm:left-[5vw] w-[calc(100vw-2rem)] sm:w-[25vw] sm:min-w-[300px] sm:max-w-[400px] bg-gradient-to-br ${bgColor} text-white rounded-xl shadow-2xl z-[60]`}>
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 animate-pulse flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm mb-1">{isInternalCall ? '内線通話中' : '通話中'}</div>
              {isInternalCall ? (
                <div className="text-lg text-white font-bold truncate mb-1">
                  {callerNumber}
                </div>
              ) : customerName ? (
                <>
                  <div className="text-lg text-white font-bold truncate mb-0.5">{customerName}</div>
                  <div className="text-sm text-white/80 truncate">{displayPhoneOnly(callerNumber)}</div>
                </>
              ) : (
                <div className="text-base text-white font-medium truncate">{displayPhoneOnly(callerNumber)}</div>
              )}
              <div className="flex items-center gap-2 text-sm mt-1">
                <Clock className="w-3.5 h-3.5" />
                <span className="font-mono font-bold">
                  {formatCallDuration(callDuration)}
                </span>
              </div>
              <div className="text-[10px] text-white/60 mt-1 truncate">ID: {currentCallId || 'N/A'}</div>
            </div>
          </div>

          {/* ミュート・スピーカー・DTMFキーパッド円ボタン */}
          <div className="flex justify-center gap-4 py-2">
            <button
              onClick={toggleMute}
              className={`w-12 h-12 rounded-full ${isMuted ? 'bg-red-500' : 'bg-white/20'} hover:bg-white/30 transition-colors flex items-center justify-center`}
              title={isMuted ? 'マイクミュート中' : 'マイクをミュート'}
            >
              {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
            <button
              onClick={toggleSpeaker}
              className={`w-12 h-12 rounded-full ${!isSpeakerOn ? 'bg-red-500' : 'bg-white/20'} hover:bg-white/30 transition-colors flex items-center justify-center`}
              title={isSpeakerOn ? 'スピーカーON' : 'スピーカーOFF'}
            >
              {isSpeakerOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            {!isInternalCall && (
              <button
                onClick={() => setIsDTMFKeypadOpen(true)}
                className="w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 transition-colors flex items-center justify-center"
                title="ダイヤルキー"
              >
                <Hash className="w-5 h-5" />
              </button>
            )}
          </div>

          <div className="space-y-2">
            {!holdQueueName && !isInternalCall && (
              <button
                onClick={handleHold}
                disabled={isHolding || !canHold}
                className="w-full px-3 py-2 bg-amber-500 hover:bg-amber-600 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Pause className="w-4 h-4" />
                {isHolding ? '保留中...' : (!canHold ? '接続待ち…' : '保留')}
              </button>
            )}
            {/* 内線通話中かつ保留がある場合: 保留解除と転送ボタンを表示 */}
            {holdQueueName && isInternalCall && (
              <>
                <button
                  onClick={handleResumeWithInternalDisconnect}
                  disabled={isResuming || isTransferring}
                  className="w-full px-3 py-2 bg-white/90 text-purple-700 hover:bg-white rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Play className="w-4 h-4" />
                  {isResuming ? '再開中...' : '保留解除（自分に転送）'}
                </button>
                <button
                  onClick={handleTransferToInternalPeer}
                  disabled={isResuming || isTransferring}
                  className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <ArrowRight className="w-4 h-4" />
                  {isTransferring ? '転送中...' : '相手に転送'}
                </button>
              </>
            )}
            {/* 外線通話中かつ保留がある場合（従来の動作） */}
            {holdQueueName && !isInternalCall && (
              <button
                onClick={handleResume}
                disabled={isResuming}
                className="w-full px-3 py-2 bg-white/90 text-emerald-700 hover:bg-white rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Play className="w-4 h-4" />
                {isResuming ? '再開中...' : '保留解除'}
              </button>
            )}
            <button
              onClick={endCall}
              className="w-full px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 shadow-md"
            >
              <PhoneOff className="w-4 h-4" />
              通話終了
            </button>
          </div>
        </div>
        </div>
        {/* DTMFキーパッド */}
        <DTMFKeypad
          isOpen={isDTMFKeypadOpen}
          onClose={() => setIsDTMFKeypadOpen(false)}
        />
      </>
    )
  }
  
  // 接続中（内線発信中も含む）
  if (effectiveCallStatus === 'connecting' || internalCallStatus === 'ringing') {
    const isInternalCall = internalCallStatus === 'ringing'
    const bgColor = isInternalCall
      ? 'from-purple-600 to-indigo-600'
      : 'from-yellow-600 to-orange-600'
    const statusText = isInternalCall ? '内線発信中...' : '接続中...'
    
    return (
      <>
        <div className={`fixed top-4 left-4 sm:left-[5vw] w-[calc(100vw-2rem)] sm:w-[25vw] sm:min-w-[300px] sm:max-w-[400px] bg-gradient-to-br ${bgColor} text-white rounded-xl shadow-2xl z-[60]`}>
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white flex-shrink-0 mt-1"></div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm mb-1">{statusText}</div>
              <div className="text-xs text-white/90 truncate">
                {isInternalCall ? `${callerNumber}さんに発信中` : (displayNumber(callerNumber) || 'キュー接続中')}
              </div>
            </div>
          </div>
          
          <div className={`grid ${!isInternalCall ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
            <button
              onClick={toggleMute}
              className={`px-2 py-1.5 ${isMuted ? 'bg-red-500' : 'bg-white/20'} hover:bg-white/30 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5`}
              title={isMuted ? 'マイクミュート中' : 'マイクをミュート'}
            >
              {isMuted ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
              {isMuted ? 'ミュート中' : 'ミュート'}
            </button>
            <button
              onClick={toggleSpeaker}
              className={`px-2 py-1.5 ${!isSpeakerOn ? 'bg-red-500' : 'bg-white/20'} hover:bg-white/30 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5`}
              title={isSpeakerOn ? 'スピーカーON' : 'スピーカーOFF'}
            >
              {isSpeakerOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              {isSpeakerOn ? 'スピーカー' : 'OFF'}
            </button>
            {!isInternalCall && (
              <button
                onClick={() => setIsDTMFKeypadOpen(true)}
                className="px-2 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
                title="ダイヤルキー"
              >
                <Hash className="w-3.5 h-3.5" />
                キー
              </button>
            )}
          </div>
          
          <div className="space-y-2">
            {!holdQueueName && !isInternalCall && (
              <button
                onClick={handleHold}
                disabled={isHolding || !canHold}
                className="w-full px-3 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                title="接続後すぐに保留できます"
              >
                <Pause className="w-4 h-4" />
                {isHolding ? '保留中...' : (!canHold ? '接続待ち…' : '保留')}
              </button>
            )}
            {/* 内線発信中かつ保留がある場合: 保留解除と転送ボタンを表示 */}
            {holdQueueName && isInternalCall && (
              <>
                <button
                  onClick={handleResumeWithInternalDisconnect}
                  disabled={isResuming || isTransferring}
                  className="w-full px-3 py-2 bg-white/90 text-purple-700 hover:bg-white rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Play className="w-4 h-4" />
                  {isResuming ? '再開中...' : '保留解除（自分に転送）'}
                </button>
                <button
                  onClick={handleTransferToInternalPeer}
                  disabled={isResuming || isTransferring}
                  className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <ArrowRight className="w-4 h-4" />
                  {isTransferring ? '転送中...' : '相手に転送'}
                </button>
              </>
            )}
            {/* 外線通話中かつ保留がある場合 */}
            {holdQueueName && !isInternalCall && (
              <button
                onClick={handleResume}
                disabled={isResuming}
                className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Play className="w-4 h-4" />
                {isResuming ? '再開中...' : '保留解除'}
              </button>
            )}
            <button
              onClick={endCall}
              className="w-full px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 shadow-md"
            >
              <PhoneOff className="w-4 h-4" />
              通話終了
            </button>
          </div>
        </div>
        </div>
        {/* DTMFキーパッド */}
        <DTMFKeypad
          isOpen={isDTMFKeypadOpen}
          onClose={() => setIsDTMFKeypadOpen(false)}
        />
      </>
    )
  }

  // 保留中（通話は切断されているが、holdQueueNameがある状態）
  if (holdQueueName && callStatus === 'idle') {
    return (
      <>
        <div className="fixed top-4 left-4 sm:left-[5vw] w-[calc(100vw-2rem)] sm:w-[25vw] sm:min-w-[300px] sm:max-w-[400px] bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-xl shadow-2xl z-[60]">
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Pause className="w-5 h-5 flex-shrink-0 mt-0.5 animate-pulse" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm mb-1">保留中</div>
              <div className="text-xs text-white/90">
                お客様が保留キューでお待ちです
              </div>
            </div>
          </div>

          <button
            onClick={handleResume}
            disabled={isResuming}
            className="w-full px-3 py-2 bg-white/90 text-amber-700 hover:bg-white rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60 shadow-md"
          >
            <Play className="w-4 h-4" />
            {isResuming ? '再開中...' : '保留解除して再接続'}
          </button>
        </div>
        </div>
        {/* DTMFキーパッド */}
        <DTMFKeypad
          isOpen={isDTMFKeypadOpen}
          onClose={() => setIsDTMFKeypadOpen(false)}
        />
      </>
    )
  }

  // 保留解除後の着信待ち
  if (isWaitingForResumedCall && callStatus === 'idle') {
    return (
      <>
        <div className="fixed top-4 left-4 sm:left-[5vw] w-[calc(100vw-2rem)] sm:w-[25vw] sm:min-w-[300px] sm:max-w-[400px] bg-gradient-to-br from-blue-500 to-cyan-600 text-white rounded-xl shadow-2xl z-[60]">
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white flex-shrink-0 mt-0.5"></div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm mb-1">着信待機中</div>
              <div className="text-xs text-white/90">
                {resumedCallInfo?.callerNumber
                  ? `${formatPhoneNumberForDisplay(resumedCallInfo.callerNumber)} からの着信を待っています...`
                  : '保留解除した通話の着信を待っています...'}
              </div>
            </div>
          </div>

          <button
            onClick={clearWaitingForResumedCall}
            className="w-full px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            キャンセル
          </button>
        </div>
        </div>
        {/* DTMFキーパッド */}
        <DTMFKeypad
          isOpen={isDTMFKeypadOpen}
          onClose={() => setIsDTMFKeypadOpen(false)}
        />
      </>
    )
  }

  return (
    <>
      {/* DTMFキーパッド */}
      <DTMFKeypad
        isOpen={isDTMFKeypadOpen}
        onClose={() => setIsDTMFKeypadOpen(false)}
      />
    </>
  )
})

export default CallStatusBar
